import { useState, useRef, useEffect, useCallback } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Video, AlertCircle, CheckCircle } from "lucide-react";
import Modal from "@/components/ui/Modal";
import {
  SessionUploadInfoCard,
  FileUploadDropZone,
  UploadErrorAlert,
  UploadModalActions,
} from "@/components/sessions/UploadModalParts";
import type { Session, SessionUsageInfo } from "@/types";
import { apiClient } from "@/services/api";
import {
  formatFileSize,
  formatUploadSpeed,
  getFileExtension,
} from "@/utils/fileUtils";
import { formatUploadDuration } from "@/utils/duration";
import { useVideoUpload } from "@/hooks/useVideoUpload";
import type { UploadStatus } from "@/hooks/useVideoUpload";

export type { UploadStatus };

interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
  onUploadComplete?: () => void;
  // Upload state props
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  uploadStatus: UploadStatus;
  setUploadStatus: (status: UploadStatus) => void;
  uploadProgress: number;
  setUploadProgress: (progress: number) => void;
  uploadSpeed: number;
  setUploadSpeed: (speed: number) => void;
  estimatedTimeRemaining: number;
  setEstimatedTimeRemaining: (time: number) => void;
  error: string;
  setError: (error: string) => void;
}

export default function VideoUploadModal({
  isOpen,
  onClose,
  session,
  onUploadComplete,
  // Upload state props
  selectedFile,
  setSelectedFile,
  uploadStatus,
  setUploadStatus,
  uploadProgress,
  setUploadProgress,
  uploadSpeed,
  setUploadSpeed,
  estimatedTimeRemaining,
  setEstimatedTimeRemaining,
  error,
  setError,
}: VideoUploadModalProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [sessionUsage, setSessionUsage] = useState<SessionUsageInfo | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchSessionUsage = useCallback(async () => {
    try {
      const usage = await apiClient.getSessionUsage(session.id);
      setSessionUsage(usage);
    } catch (err) {
      console.error("Failed to fetch session usage:", err);
    }
  }, [session.id]);

  const { start: startUpload, reset: resetUpload } = useVideoUpload(
    session.id,
    {
      setSelectedFile,
      setUploadStatus,
      setUploadProgress,
      setUploadSpeed,
      setEstimatedTimeRemaining,
      setError,
    },
    {
      onUploadComplete,
      onSessionUsageRefresh: fetchSessionUsage,
      onRequestClose: onClose,
    }
  );

  const acceptedFileTypes = [".mp4", ".mov"];
  const maxFileSize = 4 * 1024 * 1024 * 1024; // 4GB

  // Fetch session usage when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSessionUsage();
    }
  }, [isOpen, session.id, fetchSessionUsage]);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset state first
    setSelectedFile(null);
    setError("");

    // Validate file type
    const fileExtension = getFileExtension(file.name);
    if (!acceptedFileTypes.includes(fileExtension)) {
      setError(t("pages:sessions.upload.video.errorInvalidType"));
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setError(t("pages:sessions.upload.video.errorFileSize"));
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    void startUpload(selectedFile);
  };

  const handleClose = () => {
    if (uploadStatus === "error") {
      resetUpload();
    }
    onClose();
  };

  const getQuotaBarClass = (usagePercentage: number) => {
    if (usagePercentage > 80) return "bg-red-500";
    if (usagePercentage > 60) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case "uploading":
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
        );
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Video className="h-5 w-5 text-secondary-400" />;
    }
  };

  const getStatusMessage = () => {
    switch (uploadStatus) {
      case "uploading":
        return t("pages:sessions.upload.video.uploading", {
          progress: uploadProgress,
        });
      case "success":
        return t("pages:sessions.upload.video.success");
      case "error":
        return t("pages:sessions.upload.common.uploadFailed");
      default:
        return t("pages:sessions.upload.video.selectFile");
    }
  };

  const getUploadDetails = () => {
    if (uploadStatus === "uploading" && uploadSpeed > 0) {
      return (
        <div className="text-xs text-secondary-500 mt-1 space-y-1">
          <div>
            {t("pages:sessions.upload.common.speed")}{" "}
            {formatUploadSpeed(uploadSpeed)}
          </div>
          {estimatedTimeRemaining > 0 && (
            <div>
              {t("pages:sessions.upload.common.timeRemaining")}{" "}
              {formatUploadDuration(estimatedTimeRemaining)}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("pages:sessions.upload.video.title")}
      size="lg"
    >
      <div className="space-y-6">
        <SessionUploadInfoCard session={session} />

        <FileUploadDropZone
          isIdle={uploadStatus === "idle"}
          isUploading={uploadStatus === "uploading"}
          selectedFile={selectedFile}
          uploadProgress={uploadProgress}
          statusIcon={getStatusIcon()}
          statusMessage={getStatusMessage()}
          hint={t("pages:sessions.upload.video.dragDrop")}
          acceptedFileTypes={acceptedFileTypes}
          fileInputRef={fileInputRef}
          onFileSelect={handleFileSelect}
        >
          {getUploadDetails()}
        </FileUploadDropZone>

        {/* File Type Info */}
        <div className="bg-primary-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-primary-900 mb-2">
            {t("pages:sessions.upload.video.formatsTitle")}
          </h4>
          <ul className="text-xs text-primary-800 space-y-1">
            <li>
              <strong>.mp4:</strong>{" "}
              {t("pages:sessions.upload.video.mp4Desc")}
            </li>
            <li>
              <strong>.mov:</strong>{" "}
              {t("pages:sessions.upload.video.movDesc")}
            </li>
          </ul>
          <p className="text-xs text-primary-700 mt-2">
            {t("pages:sessions.upload.video.maxFileSize")}
          </p>
        </div>

        {/* Storage Limits */}
        {sessionUsage && (
          <div className="bg-secondary-50 rounded-lg p-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h4 className="text-sm font-medium text-secondary-900">
                {t("pages:sessions.upload.video.storageTitle")}
              </h4>
            </div>

            <div className="space-y-2">
              {sessionUsage && (
                <div className="grid grid-cols-[3.75rem_minmax(0,1fr)] sm:grid-cols-[4.25rem_minmax(4rem,1fr)_7.75rem] items-center gap-x-3 gap-y-1">
                  <div className="text-xs font-medium text-secondary-700">
                    {t("pages:sessions.upload.video.sessionLabel")}
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${getQuotaBarClass(sessionUsage.usagePercentage)}`}
                      style={{
                        width: `${Math.min(sessionUsage.usagePercentage, 100)}%`,
                      }}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 sm:text-right text-xs leading-tight text-secondary-600">
                    <div>
                      {formatFileSize(sessionUsage.currentSize)} /{" "}
                      {formatFileSize(sessionUsage.maxSessionSize)}
                    </div>
                    <div className="text-secondary-400">
                      {t("pages:sessions.upload.video.videosLabel", {
                        count: sessionUsage.videoCount,
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <UploadErrorAlert error={error} />

        <UploadModalActions
          busy={uploadStatus === "uploading"}
          succeeded={uploadStatus === "success"}
          showIdleActions={uploadStatus === "idle"}
          canSubmit={!!selectedFile}
          submitLabel={t("pages:sessions.upload.video.submit")}
          onBrowse={() => fileInputRef.current?.click()}
          onSubmit={handleUpload}
          onClose={handleClose}
        />
      </div>
    </Modal>
  );
}
